import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type {
  GraphQLCommitment,
  GraphQLSupporterGiftLimits,
  GraphQLSupporterGiftTypeLimits,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isItemGiftCenter } from '@sola/shared-utils/cart/cart-item-type-guards.ts';
import {
  FinalGiftIneligibilityCode,
  isFinalGift,
} from '@sola/shared-utils/my/gifts-type-guards.ts';
import {
  isSponsorshipCommitmentGiftable,
  sortSponsorshipCommitmentsByBirthdate,
} from '@sola/supporter-sdk/utils/commitments.ts';

import type { ChildGiftOptionGroups, GiftOption } from '../../types/gifts.ts';
import {
  applyCenterCartAwareness,
  applyChildCartAwareness,
} from './gift-availability-cart.ts';
import { resolveGiftOption } from './gift-availability-rules.ts';

type FormattedGiftOptions = Partial<Record<GiftTypeEnum, GiftOption>>;

const resolveOption =
  (giftType: GiftTypeEnum) =>
  (
    giftTypes: GraphQLSupporterGiftTypeLimits,
    defaults: GraphQLSupporterGiftLimits['defaults'],
  ): GiftOption | undefined => {
    const limits = giftTypes[giftType];
    const defaultLimits = defaults[giftType];

    if (!limits || !defaultLimits) {
      return undefined;
    }

    if (
      isFinalGift(giftType) &&
      limits.ineligibilityCode !== '' &&
      limits.ineligibilityCode !== FinalGiftIneligibilityCode.alreadyGiven
    ) {
      return undefined;
    }

    return resolveGiftOption(
      giftType,
      limits,
      defaultLimits,
      giftTypes,
      defaults,
    );
  };

const resolveOptionBirthday = resolveOption(GiftTypeEnum.Birthday);
const resolveOptionChild = resolveOption(GiftTypeEnum.Child);
const resolveOptionFamily = resolveOption(GiftTypeEnum.Family);
const resolveOptionCenter = resolveOption(GiftTypeEnum.Center);
const resolveOptionFinal = resolveOption(GiftTypeEnum.Final);

const resolveCenterGift = (
  centerGiftTypes: GraphQLSupporterGiftTypeLimits | undefined,
  defaults: GraphQLSupporterGiftLimits['defaults'],
  centerId: string | undefined,
): GiftOption | undefined => {
  if (!centerGiftTypes) {
    return undefined;
  }

  const center = resolveOptionCenter(centerGiftTypes, defaults);

  if (!center) {
    return undefined;
  }

  return centerId ? { ...center, centerId } : center;
};

const resolveChildGifts = (
  globalId: GraphQLCommitment['globalId'],
  giftTypes: GraphQLSupporterGiftTypeLimits,
  defaults: GraphQLSupporterGiftLimits['defaults'],
  cartItems: CartItem[],
  centerId: string | undefined,
  isAssociatedWithCenterGift: boolean,
  centerGiftTypes?: GraphQLSupporterGiftTypeLimits,
): FormattedGiftOptions => {
  const birthday = resolveOptionBirthday(giftTypes, defaults);
  const child = resolveOptionChild(giftTypes, defaults);
  const family = resolveOptionFamily(giftTypes, defaults);
  const final = resolveOptionFinal(giftTypes, defaults);
  const center = resolveCenterGift(centerGiftTypes, defaults, centerId);

  const apply = (giftType: GiftTypeEnum, option: GiftOption | undefined) =>
    applyChildCartAwareness(
      giftType,
      option,
      cartItems,
      globalId,
      giftTypes,
      defaults,
    );

  const birthdayView = apply(GiftTypeEnum.Birthday, birthday);
  const childView = apply(GiftTypeEnum.Child, child);
  const familyView = apply(GiftTypeEnum.Family, family);
  const finalView = apply(GiftTypeEnum.Final, final);
  const centerView =
    center && centerId
      ? applyCenterCartAwareness(
          center,
          cartItems,
          centerId,
          globalId,
          isAssociatedWithCenterGift,
        )
      : center;

  return {
    ...(birthdayView && { [GiftTypeEnum.Birthday]: birthdayView }),
    ...(childView && { [GiftTypeEnum.Child]: childView }),
    ...(familyView && { [GiftTypeEnum.Family]: familyView }),
    ...(finalView && { [GiftTypeEnum.Final]: finalView }),
    ...(centerView && { [GiftTypeEnum.Center]: centerView }),
  };
};

/** NOTE: Used in the useGiftAvailability hook to build formatted gift options used by the component */
export const formatGiftOptions = (
  giftLimits?: GraphQLSupporterGiftLimits,
  commitments: GraphQLCommitment[] = [],
  cartItems: CartItem[] = [],
  childCenterMap: Record<string, string> = {},
): ChildGiftOptionGroups => {
  const defaultGiftOptions = {
    [GiftTypeEnum.Birthday]: [],
    [GiftTypeEnum.Child]: [],
    [GiftTypeEnum.Family]: [],
    [GiftTypeEnum.Center]: [], // Center gift options are resolved at the child level since they depend on the center
    [GiftTypeEnum.Final]: [],
  };

  if (!giftLimits) {
    return defaultGiftOptions;
  }

  const giftableCommitments = commitments.filter(
    isSponsorshipCommitmentGiftable,
  );
  const sortedCommitments =
    sortSponsorshipCommitmentsByBirthdate(giftableCommitments);

  const centerGiftAssociationMap = cartItems.reduce<Record<string, string>>(
    (acc, item) => {
      if (!isItemGiftCenter(item) || item.child) {
        return acc;
      }
      const centerId = item.gift.centerId;
      if (acc[centerId]) {
        return acc;
      }

      const associatedChild = Object.entries(childCenterMap).find(
        ([, cId]) => cId === centerId,
      )?.[0];
      return associatedChild ? { ...acc, [centerId]: associatedChild } : acc;
    },
    {},
  );

  return sortedCommitments.reduce<ChildGiftOptionGroups>(
    (giftOptions, commitment) => {
      if (!commitment.participant?.globalId) {
        return giftOptions;
      }

      const { globalId } = commitment.participant;
      const childGiftLimitGroup = giftLimits.children.find(
        (child) => child.id === globalId,
      );
      const giftTypes = childGiftLimitGroup?.giftTypes;

      if (!giftTypes) {
        return giftOptions;
      }

      const centerId =
        childCenterMap[globalId] ?? childGiftLimitGroup?.centerId;
      const centerGiftTypes =
        giftLimits.projects.find((project) => project.id === centerId)
          ?.giftTypes ?? giftLimits.defaults;

      const isAssociatedWithCenterGift = centerId
        ? !centerGiftAssociationMap[centerId] ||
          centerGiftAssociationMap[centerId] === globalId
        : true;

      const giftAvailabilityForChild = resolveChildGifts(
        globalId,
        giftTypes,
        giftLimits.defaults,
        cartItems,
        centerId,
        isAssociatedWithCenterGift,
        centerGiftTypes,
      );

      for (const [giftType, giftOption] of Object.entries(
        giftAvailabilityForChild,
      ) as [GiftTypeEnum, GiftOption][]) {
        giftOptions[giftType].push({
          commitment,
          giftOption,
        });
      }

      return giftOptions;
    },
    defaultGiftOptions,
  );
};
